import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Bookmark,
  CheckCircle2,
  Eye,
  MessageSquare,
  ShieldCheck,
  ThumbsUp,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FeedPost } from '@up2d8/contracts';
import {
  AttachmentRowBody,
  Callout,
  PostHead,
  attachmentRowClass,
  cn,
  fileSizeLabel,
  relativeTime,
} from '@up2d8/ui';
import { useFileUrl } from '../../../shared/use-upload';
import { useFeedCounts, useLikes, useToggleLike, useToggleSaved } from '../app-interaction';
import { formatDateTime } from '../../../shared/format';
import { SafeHtml } from '../../../shared/safe-html';
import { useLinkClickTracker } from '../../../shared/link-clicks';

/**
 * De detailpagina van een bericht op een bureauscherm (bericht-detail-spec).
 *
 * **Geen herbouw.** De mobiele pagina bestaat en werkt — bevestigen, liken, reageren, vertalen,
 * de transparantieregel. Dit is dezelfde pagina in de bureauschil met de opmaak van de spec; de
 * gegevens komen uit dezelfde hooks met dezelfde cachesleutels, zodat een teller hier nooit iets
 * anders kan zeggen dan op de kaart in de tijdlijn.
 */
export interface DeskPostDetailProps {
  postId: string;
  post: FeedPost;
  /** De bevestigingsbalk staat er alleen als er iets te bevestigen is. */
  needsAcknowledgement: boolean;
  /**
   * Wanneer deze medewerker bevestigde, en op welke versie. Leeg zolang hij dat niet deed.
   *
   * Dat dit er staat en niet alleen de balk verdwijnt, is het punt: wie net op "Gelezen en
   * begrepen" drukte, hoort te zien dat het geregistreerd is. Een knop die zonder spoor weggaat,
   * laat iemand nog een keer drukken - en bij een bevestiging die bewijs is, is dat het verschil
   * tussen vertrouwen en twijfel.
   */
  acknowledgedAt?: string | null;
  acknowledgedVersion?: number | null;
  acknowledging: boolean;
  onAcknowledge: () => void;
  onBack: () => void;
  /** De vertaalregels van de mobiele pagina; ze gelden hier even hard (ADR-0043). */
  translationNotice?: React.ReactNode;
}

export function DeskPostDetail({
  postId,
  post,
  needsAcknowledgement,
  acknowledgedAt = null,
  acknowledgedVersion = null,
  acknowledging,
  onAcknowledge,
  onBack,
  translationNotice,
}: DeskPostDetailProps) {
  const { t } = useTranslation();
  const trackLink = useLinkClickTracker(post.postId);

  const ids = [postId];
  const likes = useLikes(ids);
  const counts = useFeedCounts(ids);
  const toggleLike = useToggleLike(postId);

  const like = likes.data?.[0];
  const postCounts = counts.data?.[0];

  const save = useToggleSaved(postId);

  return (
    // §2: één kolom van 820, gecentreerd. Geen rechterkolom — dit scherm heeft er niets aan.
    <div className="mx-auto w-full max-w-[820px]">
      {/* §3: de terugknop als omlijnde pill. */}
      <button
        type="button"
        onClick={onBack}
        data-testid="back-to-home"
        className="mb-[18px] inline-flex items-center gap-1.5 rounded-pill border border-border-light bg-surface px-3.5 py-[7px] font-heading text-caption font-medium text-text-secondary shadow-sm transition-colors hover:border-brand-primary-tint-deep hover:text-brand-primary"
      >
        <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
        {t('interaction.backToHome')}
      </button>

      <article
        data-testid="post-card"
        className="rounded-card border border-border-light bg-surface shadow-sm"
      >
        {/* §4.1 — dezelfde kop als op de kaart in de tijdlijn. */}
        <PostHead
          author={post.author ?? null}
          time={relativeTime(post.publishedAt, new Date(), {
            justNow: t('feed.justNow'),
            minutesAgo: t('feed.minutesAgo'),
            hoursAgo: t('feed.hoursAgo'),
            yesterday: t('feed.yesterday'),
          })}
          audience={post.audienceDescription ?? null}
        />

        {/*
          §4.2 — de titel, 22 px tegenover 15 op de kaart.

          `text-page-title` en niet een vaste 22: op een telefoon tilt de werkvloervloer die naar
          24, en dat is precies wat ADR-0065 bedoelt.
        */}
        <h1
          data-testid="post-title"
          className="px-5 pb-1.5 font-heading text-page-title font-semibold leading-[1.3] text-text-primary"
        >
          {post.title}
        </h1>

        {/* `empty:hidden`: de vertaalregels geven niets terug bij een bericht in de eigen taal. */}
        {translationNotice ? (
          <div className="px-5 pb-2 empty:hidden">{translationNotice}</div>
        ) : null}

        {/* Ingetrokken en vervangen (Extra 3): dezelfde regel als op de telefoon. */}
        {post.replacedBy ? (
          <div className="px-5 pb-3">
            <Callout
              tone="warning"
              data-testid="post-replaced-by"
              title={t('posts.replacedByTitle')}
            >
              <Link
                to="/app/posts/$postId"
                params={{ postId: post.replacedBy.postId }}
                className="font-medium text-brand-primary underline"
              >
                {post.replacedBy.title}
              </Link>
            </Callout>
          </div>
        ) : null}

        {/* §4.3 — de tekst. Door de witte lijst van ADR-0037 heen; nooit rauwe HTML. */}
        <SafeHtml
          data-testid="post-body"
          className="prose-up2d8 px-5 pb-3.5 text-small leading-[1.55] text-text-secondary"
          html={post.bodyHtml}
          onClick={trackLink}
        />

        {/* §4.4 — de bevestigingsbalk. Zonder datum: een bevestigingsdeadline bestaat niet in het
            model, en een verzonnen termijn is erger dan geen termijn. */}
        {needsAcknowledgement && (
          <div
            data-testid="desk-ack-bar"
            className="mx-5 mb-3.5 flex items-center gap-3 rounded-md border border-brand-orange-soft bg-[linear-gradient(90deg,var(--brand-orange-tint),transparent)] px-3.5 py-3"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-brand-orange text-text-on-dark">
              <CheckCircle2 aria-hidden className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <p className="min-w-0 flex-1 text-caption text-text-secondary">
              <span
                data-testid="desk-ack-kop"
                className="font-heading font-semibold text-brand-orange-deep"
              >
                {t('feed.ackNeeded')}
              </span>{' '}
              {t('acknowledge.notYet')}
            </p>
            <button
              type="button"
              data-testid="desk-ack-button"
              disabled={acknowledging}
              onClick={onAcknowledge}
              className="shrink-0 rounded-pill bg-brand-orange px-3.5 py-[7px] font-heading text-caption font-semibold text-text-on-dark transition-colors hover:bg-brand-orange-deep disabled:opacity-60"
            >
              {t('acknowledge.button')}
            </button>
          </div>
        )}

        {acknowledgedAt && (
          <p
            data-testid="desk-ack-done"
            className="mx-5 mb-3.5 flex items-center gap-2 rounded-md bg-success-tint px-3.5 py-2.5 text-caption text-text-primary"
          >
            <CheckCircle2 aria-hidden className="h-5 w-5 shrink-0 text-success" />
            {t('acknowledge.confirmedAt', {
              when: formatDateTime(acknowledgedAt),
              version: acknowledgedVersion ?? 1,
            })}
          </p>
        )}

        {/*
          Transparantie (ADR-0014): wie iets over je registreert, zegt dát hij dat doet en waar je
          leest wat er bijgehouden wordt. Dezelfde regel als op de telefoon; op het bureauscherm
          ontbrak ze (testrapport 28 okt., #12).
        */}
        {post.requiresAcknowledgement && (
          <p
            data-testid="desk-ack-transparency"
            className="mx-5 mb-3.5 flex items-start gap-2 rounded-md bg-brand-primary-tint px-3.5 py-2.5 text-caption text-text-secondary"
          >
            <ShieldCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-brand-primary" />
            <span>
              {t('acknowledge.transparency')}{' '}
              <Link to="/app/privacy/werkgever" className="text-brand-primary underline">
                {t('acknowledge.transparencyLink')}
              </Link>
            </span>
          </p>
        )}

        {/* §4.5 — de bijlagen. Geen paginatelling, om dezelfde reden als op de kaart: een getal
            dat soms klopt en soms ontbreekt, leest als een fout. */}
        {post.attachments.length > 0 && (
          <ul className="flex flex-col gap-1.5 px-5 pb-3.5">
            {post.attachments.map((file) => (
              <li key={file.fileId}>
                <DeskAttachment file={file} />
              </li>
            ))}
          </ul>
        )}

        {/* §4.6 — de actiebalk. */}
        <div
          data-testid="post-actions"
          className="flex items-center gap-1.5 border-y border-border-light px-5 py-3"
        >
          <ActionButton
            testId="desk-like"
            active={like?.likedByMe ?? false}
            onClick={() => toggleLike.mutate(!(like?.likedByMe ?? false))}
            icon={
              <ThumbsUp
                aria-hidden
                className="h-[15px] w-[15px]"
                fill={like?.likedByMe ? 'currentColor' : 'none'}
              />
            }
            label={t('interaction.like')}
          />
          <ActionButton
            testId="desk-comment"
            onClick={() => {
              document.getElementById('reacties')?.scrollIntoView({ block: 'start' });
            }}
            icon={<MessageSquare aria-hidden className="h-[15px] w-[15px]" />}
            label={t('interaction.reply')}
          />
          <ActionButton
            testId="desk-save"
            active={post.savedByMe}
            onClick={() => save.mutate(!post.savedByMe)}
            icon={
              <Bookmark
                aria-hidden
                className="h-[15px] w-[15px]"
                fill={post.savedByMe ? 'currentColor' : 'none'}
              />
            }
            label={post.savedByMe ? t('interaction.saved') : t('interaction.save')}
          />

          {/*
            Rechts de weergaven en bevestigingen. Ze verdwijnen onder vijf ontvangers (ADR-0041);
            dat gedrag zit in de hooks en wordt hier niet opnieuw bedacht — `null` betekent "te
            weinig mensen om een getal te tonen", en dan staat er niets in plaats van een nul.
          */}
          <span className="ml-auto flex items-center gap-3.5 text-caption text-text-muted">
            {postCounts?.viewCount !== null && postCounts?.viewCount !== undefined && (
              <span className="flex items-center gap-1" data-testid="desk-views">
                <Eye aria-hidden className="h-3 w-3" />
                {postCounts.viewCount}
              </span>
            )}
            {postCounts?.acknowledgedCount !== null &&
              postCounts?.acknowledgedCount !== undefined && (
                <span className="flex items-center gap-1" data-testid="desk-acks">
                  {/* Een lijn-icoon, en niet het gevulde vinkje uit de mockup: dat staat daar
                      per ongeluk gevuld en verschijnt als een gevuld hoekje. Een renderingsfout
                      overnemen is geen replica maar imitatie. */}
                  <CheckCircle2 aria-hidden className="h-3 w-3" />
                  {t('feed.acknowledgedCount', { count: postCounts.acknowledgedCount })}
                </span>
              )}
          </span>
        </div>
      </article>
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  active,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-3.5 py-2 font-heading text-caption font-medium transition-colors',
        active
          ? 'bg-brand-primary-tint text-brand-primary'
          : 'text-text-secondary hover:bg-brand-primary-tint hover:text-brand-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function DeskAttachment({ file }: { file: FeedPost['attachments'][number] }) {
  const { t } = useTranslation();
  const fileUrl = useFileUrl();

  return (
    <button
      type="button"
      data-testid="desk-attachment"
      className={cn('w-full', attachmentRowClass)}
      onClick={async () => {
        const link = await fileUrl(file.fileId);
        if (link) window.open(link, '_blank', 'noopener,noreferrer');
      }}
    >
      <AttachmentRowBody
        name={file.fileName}
        isImage={file.isImage}
        meta={fileSizeLabel(file.sizeBytes)}
      />
      <span className="sr-only">{t('feed.openAttachment')}</span>
    </button>
  );
}
